import React, { useState, useMemo } from 'react';
import { Send } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle,
  Button, ICON_PX,
} from '../../../ui';
import { useInbox } from '../InboxContext';
import { formatDateTime } from '../../../../utils/formatters';
import { apiFetch } from '../../../../services/apiClient';
import { useAuth } from '../../../../contexts/AuthContext';

function ComposeDialog() {
  const { compose, closeCompose } = useInbox();

  if (!compose) return null;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) closeCompose(); }}>
      <DialogContent className="max-w-lg">
        <ComposeForm />
      </DialogContent>
    </Dialog>
  );
}

function ComposeForm() {
  const { compose, closeCompose } = useInbox();
  const { getToken } = useAuth();
  const { mode, email } = compose;

  const isNew = mode === 'new';

  const defaultTo = useMemo(() => {
    if (isNew || mode === 'forward') return '';
    if (mode === 'replyAll' && email) {
      const all = [email.from.email, ...email.to.map((t) => t.email), ...(email.cc || []).map((c) => c.email)];
      return [...new Set(all)].filter((e) => e !== 'pm@sunridgeclinic.org').join(', ');
    }
    return email?.from?.email ?? '';
  }, [mode, email, isNew]);

  const defaultSubject = useMemo(() => {
    if (isNew || !email) return '';
    const subj = email.subject.replace(/^(Re:|Fwd:)\s*/i, '');
    return mode === 'forward' ? `Fwd: ${subj}` : `Re: ${subj}`;
  }, [mode, email, isNew]);

  const defaultBody = useMemo(() => {
    if (isNew || !email) return '';
    if (mode === 'forward') {
      return `\n\n---------- Forwarded message ----------\nFrom: ${email.from.name} <${email.from.email}>\nDate: ${formatDateTime(email.receivedAt)}\nSubject: ${email.subject}\nTo: ${email.to.map((t) => `${t.name} <${t.email}>`).join(', ')}\n\n${email.bodyText || ''}`;
    }
    return '';
  }, [mode, email, isNew]);

  const [to, setTo] = useState(defaultTo);
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  const [showCc, setShowCc] = useState(false);
  const [sent, setSent] = useState(false);

  const title = isNew ? 'New Message' : mode === 'forward' ? 'Forward' : mode === 'replyAll' ? 'Reply All' : 'Reply';

  const handleSend = () => {
    if (!to.trim()) return;
    setSent(true);
    // Persist to backend (fire-and-forget)
    apiFetch('/api/emails/send', {
      method: 'POST',
      body: JSON.stringify({
        to: to.trim(),
        cc: cc.trim() || undefined,
        subject: subject.trim() || undefined,
        body: body,
        replyToId: (!isNew && email?.id) ? email.id : undefined,
      }),
    }, getToken).catch(() => {});
    setTimeout(closeCompose, 1500);
  };

  if (sent) {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Message Sent</DialogTitle>
        </DialogHeader>
        <div className="px-6 py-8 text-center">
          <span className="text-[13px] font-semibold text-green-nn">Your message has been sent successfully.</span>
        </div>
      </>
    );
  }

  const fieldClass = 'flex-1 min-w-0 bg-nn-white rounded-md px-2.5 py-1.5 border border-nn-border text-[12px] text-text-dark focus:outline-none focus:ring-1 focus:ring-aqua/40 focus:border-aqua transition-colors';

  return (
    <>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
      </DialogHeader>

      <div className="flex flex-col gap-2.5 px-6 py-3">
        {/* To */}
        <div className="flex items-center gap-2 text-[12px]">
          <span className="text-text-muted shrink-0 w-10 text-right">To</span>
          <input type="text" value={to} onChange={(e) => setTo(e.target.value)}
            className={fieldClass} placeholder="recipient@example.com" autoFocus />
          {!showCc && (
            <button type="button" onClick={() => setShowCc(true)} className="text-[11px] text-aqua hover:underline shrink-0">Cc</button>
          )}
        </div>

        {/* CC */}
        {showCc && (
          <div className="flex items-center gap-2 text-[12px]">
            <span className="text-text-muted shrink-0 w-10 text-right">Cc</span>
            <input type="text" value={cc} onChange={(e) => setCc(e.target.value)}
              className={fieldClass} placeholder="cc@example.com" />
          </div>
        )}

        {/* Subject */}
        <div className="flex items-center gap-2 text-[12px]">
          <span className="text-text-muted shrink-0 w-10 text-right">Subject</span>
          <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
            className={fieldClass} placeholder="Email subject" />
        </div>

        {/* Body */}
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={10}
          className="w-full bg-nn-white rounded-md px-2.5 py-2 border border-nn-border text-[12px] text-text-dark focus:outline-none focus:ring-1 focus:ring-aqua/40 focus:border-aqua resize-none font-body leading-relaxed transition-colors"
          placeholder="Write your message…"
        />
      </div>

      <DialogFooter>
        <Button variant="default" size="sm" onClick={closeCompose}>Cancel</Button>
        <Button variant="save" size="sm" onClick={handleSend} disabled={!to.trim()}>
          <Send size={ICON_PX.sm} className="mr-1" /> Send
        </Button>
      </DialogFooter>
    </>
  );
}

export { ComposeDialog };
